import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { features } from '@ircf/config';
import { sessionStore } from '@/lib/storage.js';

/**
 * Device and preference capability detection.
 *
 * Every decision about whether to render WebGL, how hard to drive it, and
 * whether to animate at all is made here once and read everywhere else. Scene
 * components must not probe the environment themselves, or the public routes end
 * up with several inconsistent notions of "this device is slow".
 */

export type QualityTier = 'off' | 'low' | 'medium' | 'high';

export interface Capability {
  /** WebGL2 (or WebGL1) context could actually be created. */
  hasWebGl: boolean;
  /** `prefers-reduced-motion: reduce` is set. */
  prefersReducedMotion: boolean;
  /** Coarse pointer, i.e. touch-first. Hover affordances must have alternatives. */
  isTouch: boolean;
  /** Viewport is below the `md` breakpoint. */
  isNarrow: boolean;
  /** Effective quality tier, `off` when 3D must not render at all. */
  tier: QualityTier;
  /** Device pixel ratio, capped so a 3× phone screen does not render 9× the pixels. */
  pixelRatio: number;
  /** True once the renderer has reported sustained low frame rates. */
  isDegraded: boolean;
  /** Called by the performance monitor to step quality down one tier. */
  reportPoorPerformance: () => void;
  /** Explicit user opt-out, persisted for the tab session. */
  animationsDisabled: boolean;
  setAnimationsDisabled: (value: boolean) => void;
}

const CapabilityCtx = createContext<Capability | null>(null);

/**
 * Probes for WebGL by creating a throwaway context. Feature-detecting the
 * constructor is not enough: browsers with WebGL disabled by policy still expose
 * it, and blocklisted GPUs fail only on context creation.
 */
function detectWebGl(): boolean {
  if (typeof document === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    const context =
      canvas.getContext('webgl2') ??
      canvas.getContext('webgl') ??
      canvas.getContext('experimental-webgl');
    if (!context) return false;
    // Release immediately; probing must not hold a GPU context open.
    const lose = (context as WebGLRenderingContext).getExtension('WEBGL_lose_context');
    lose?.loseContext();
    return true;
  } catch {
    return false;
  }
}

/**
 * Chooses a starting tier from cheap, stable signals. Deliberately conservative:
 * the performance monitor can only reduce quality, never raise it, so a wrong
 * guess degrades gracefully instead of thrashing.
 */
function detectInitialTier(input: {
  hasWebGl: boolean;
  isNarrow: boolean;
  isTouch: boolean;
}): QualityTier {
  if (!input.hasWebGl) return 'off';

  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  const cores = navigator.hardwareConcurrency ?? 4;
  const saveData = (
    navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }
  ).connection;

  if (saveData?.saveData) return 'low';
  if (saveData?.effectiveType && /^(slow-)?2g$/.test(saveData.effectiveType)) return 'low';
  if (cores <= 4 || (memory !== undefined && memory <= 4)) return 'low';
  if (input.isNarrow || input.isTouch) return 'medium';
  return 'high';
}

function stepDown(tier: QualityTier): QualityTier {
  switch (tier) {
    case 'high':
      return 'medium';
    case 'medium':
      return 'low';
    default:
      return 'low';
  }
}

/** Session-scoped, so the opt-out lasts the visit without becoming a durable profile. */
const ANIMATION_PREFERENCE_KEY = 'animations';

const NARROW_QUERY = '(max-width: 767px)';
const TOUCH_QUERY = '(hover: none), (pointer: coarse)';
const MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Subscribes to a media query without holding a copy of its result in state.
 *
 * `useSyncExternalStore` rather than `useState` + `useEffect`: the browser is
 * already the source of truth, and mirroring it into state means a first render
 * with a stale answer followed by a corrective one. For the reduced-motion query
 * that is not cosmetic - it is one frame of animation shown to someone who asked
 * for none.
 */
function useMediaQuery(query: string, initial = false): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined') return () => undefined;
      const list = window.matchMedia(query);
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );

  const getSnapshot = useCallback(
    () => (typeof window === 'undefined' ? initial : window.matchMedia(query).matches),
    [query, initial],
  );

  const getServerSnapshot = useCallback(() => initial, [initial]);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function CapabilityProvider({ children }: { children: ReactNode }): JSX.Element {
  const prefersReducedMotion = useMediaQuery(MOTION_QUERY);
  const isNarrow = useMediaQuery(NARROW_QUERY);
  const isTouch = useMediaQuery(TOUCH_QUERY);

  // Probed once: WebGL availability does not change within a page lifetime.
  const [hasWebGl] = useState(detectWebGl);
  const [degradations, setDegradations] = useState(0);
  const [animationsDisabled, setAnimationsDisabled] = useState(
    () => sessionStore.read(ANIMATION_PREFERENCE_KEY) === 'off',
  );

  useEffect(() => {
    if (animationsDisabled) sessionStore.write(ANIMATION_PREFERENCE_KEY, 'off');
    else sessionStore.remove(ANIMATION_PREFERENCE_KEY);
  }, [animationsDisabled]);

  const value = useMemo<Capability>(() => {
    const enabledByFlag = features.threeDimensional;
    const suppressed = !enabledByFlag || animationsDisabled;

    let tier: QualityTier = suppressed
      ? 'off'
      : detectInitialTier({ hasWebGl, isNarrow, isTouch });

    for (let step = 0; step < degradations && tier !== 'off'; step += 1) {
      tier = stepDown(tier);
    }

    return {
      hasWebGl,
      prefersReducedMotion: prefersReducedMotion || animationsDisabled,
      isTouch,
      isNarrow,
      tier,
      // Above 2× the visual gain is negligible and the fill cost is quadratic.
      pixelRatio: Math.min(typeof window === 'undefined' ? 1 : window.devicePixelRatio, 2),
      isDegraded: degradations > 0,
      reportPoorPerformance: () => setDegradations((count) => Math.min(count + 1, 2)),
      animationsDisabled,
      setAnimationsDisabled,
    };
  }, [hasWebGl, isNarrow, isTouch, prefersReducedMotion, degradations, animationsDisabled]);

  return <CapabilityCtx.Provider value={value}>{children}</CapabilityCtx.Provider>;
}

export function useCapability(): Capability {
  const context = useContext(CapabilityCtx);
  if (!context) {
    throw new Error('useCapability must be used within <CapabilityProvider>');
  }
  return context;
}

/** True when a scene should render WebGL at all. */
export function useCanRender3d(): boolean {
  const { tier, hasWebGl } = useCapability();
  return hasWebGl && tier !== 'off';
}
